import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { cloneDeep, last } from 'lodash';

import { getBlogProjectEditingDrafts } from 'src/data/blog';
import { SOCIAL_CHANNEL } from 'src/data/channel';
import { GENERATION_TOOL, isRewriteGenerationTool } from 'src/data/generationTool';
import {
  DEFAULT_IMAGE_ASPECT_RATIO,
  DEFAULT_IMAGE_STYLE_ID,
  IMAGE_ASPECT_RATIO,
  initImageOverlayProps,
} from 'src/data/image';
import {
  getBareboneProject,
  isBareboneProject,
  prepareLoadedProject,
  PROJECT_STATUS,
  PROJECT_TYPE,
  unwrapProjects,
} from 'src/data/project';
import { DEFAULT_SUGGEST_TOV_MODE, DRAFT_TOV_ID } from 'src/data/toneOfVoice';
import {
  deleteProject,
  queryAssociatedProjects,
  queryProject,
  queryProjectCommonData,
  queryProjectOptimizationData,
  queryProjects,
  updateProject,
  updateProjectBrandVoicesForBrandTerms,
  updateProjectFavorite,
} from 'src/graphql/project';
import { parseJson } from 'src/lib/common';
import { isAbortError } from 'src/lib/errors';

const NAME = 'global-project';

const parseGenerationInfo = (project) => {
  try {
    const parsed = parseJson(project?.brief?.generationInfo);
    return parsed || null;
  } catch {
    return null;
  }
};

const getAdditionalProjectDataFunction = (projectType) => {
  switch (projectType) {
    case PROJECT_TYPE.continuousOptimization:
    case PROJECT_TYPE.wtmHomePage:
      return queryProjectOptimizationData;

    // A long-form project only shows the migration popup, which needs nothing more.
    case PROJECT_TYPE.longFormBuilder:
      return null;

    case PROJECT_TYPE.document:
    default:
      return queryProjectCommonData;
  }
};

const queryAdditionalProjectData = ({ project }, options) => {
  const queryFunction = getAdditionalProjectDataFunction(project.type);
  if (!queryFunction) {
    return Promise.resolve(project);
  }

  return queryFunction({ projectId: project.id }, options).then((data) => ({
    ...project,
    ...data,
  }));
};

const isProjectReady = (project) => {
  // "Blog builder" projects can have only two statuses - "draft" and "editing"
  //   both of which mean "ready" in this case
  if (
    project.type === PROJECT_TYPE.blogPostBuilder ||
    project.type === PROJECT_TYPE.longFormBuilder ||
    project.type === PROJECT_TYPE.document ||
    // TODO: Remove once no migrated to document_editor
    project.type === PROJECT_TYPE.canvas
  ) {
    return true;
  }

  // Both "done" and "legacy" statuses should be treated as project is ready to display on FE
  return [PROJECT_STATUS.done, PROJECT_STATUS.legacy].includes(project.status);
};

const INITIAL_STATE = {
  projects: [],
  loadingProjects: false,
  loadedProjects: false,
  loadingMoreProjects: false,
  projectsCursor: null,
  hasMoreProjects: false,
  processingIds: [],

  newProjectPromptData: null,
  // Stores the prompt text that was already classified (user chose "Stay with prompt")
  // Used to skip re-classification if prompt hasn't changed
  preClassifiedPromptText: null,
  // Stores the generation tool override for new project (e.g., from template classification)
  // Used to generate with a different tool while staying on freestyle UI
  newProjectGenerationToolOverride: null,
  currentProject: null,
  queryCurrentProjectAbortController: null,
  loadingCurrentProject: false,
  isCurrentProjectEmpty: true,

  toneOfVoiceMenuOpened: false,
  selectedTov: DEFAULT_SUGGEST_TOV_MODE,
  selectedCustomModelId: null,
  draftToneOfVoice: null,
  toneOfVoiceError: null,

  searching: false,
  searchResult: [],

  isBlogProjectDraft: false,
  blogDraftId: null,

  selectedImageStyleId: DEFAULT_IMAGE_STYLE_ID,
  imageAspectRatio: DEFAULT_IMAGE_ASPECT_RATIO,
  imageOverlayProperties: initImageOverlayProps(),

  imageGenerationToolOptionsCache: {},
};

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  addProjectToCache:
    (project) =>
    ({ getState, setState }) => {
      const { projects: list } = getState();
      const newList = [project, ...list];
      setState({ projects: newList });
    },

  mergeProjectInCache:
    (project) =>
    ({ getState, setState }) => {
      const { projects: list, currentProject } = getState();
      const projectIndex = list.findIndex((p) => p.id === project.id);
      if (projectIndex >= 0) {
        const cacheProject = list[projectIndex];
        list[projectIndex] = {
          ...cacheProject,
          ...project,
        };
      }
      setState({ projects: [...list] });

      if (currentProject?.id === project.id) {
        setState({ currentProject: { ...currentProject, ...project } });
      }
    },

  deleteProjectFromCache:
    (project) =>
    ({ getState, setState }) => {
      const { projects: list } = getState();
      const newList = list.filter((p) => p.id !== project.id);
      setState({ projects: newList });
    },

  markProjectProcessing:
    (id, processing) =>
    ({ getState, setState }) => {
      const { processingIds } = getState();
      const newList = processing ? [...processingIds, id] : processingIds.filter((n) => n !== id);
      setState({ processingIds: newList });
    },

  clearToneOfVoiceError:
    () =>
    ({ setState }) => {
      setState({ toneOfVoiceError: null });
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  resetLoadedProjects:
    () =>
    ({ setState }) => {
      setState({ loadedProjects: false });
    },

  queryProjects:
    ({
      customerId,
      workspaceId,
      onlyForCurrentUser,
      favoritesOnly,
      projectTypes,
      shouldQueryAssociatedProjects,
      projectFolderId,
      sortOrder,
      sortField,
      withAssetsGenerationTools,
    }) =>
    async ({ setState, dispatch }) => {
      if (!customerId) {
        dispatch(ACTIONS.clearStore());
        throw new Error('Unknown customer');
      }

      const filter = {
        currentUser: onlyForCurrentUser,
        favoriteOnly: favoritesOnly,
        types: projectTypes,
      };

      setState({
        loadingProjects: true,
        loadedProjects: false,
      });
      try {
        // For WTM projects we should use another query
        const query = shouldQueryAssociatedProjects ? queryAssociatedProjects : queryProjects;

        const result = await query({
          customerId,
          workspaceId,
          cursor: null,
          filter,
          projectFolderId,
          sortOrder,
          sortField,
          withAssetsGenerationTools,
        });

        const { edges, pageInfo } = result;
        const projects = unwrapProjects(edges);

        setState({
          projects,
          hasMoreProjects: pageInfo.hasNextPage,
          projectsCursor: pageInfo.endCursor,
        });

        return projects;
      } finally {
        setState({
          loadingProjects: false,
          loadedProjects: true,
        });
      }
    },

  queryMoreProjects:
    ({
      workspaceId,
      onlyForCurrentUser,
      favoritesOnly,
      projectTypes,
      shouldQueryAssociatedProjects,
      projectFolderId,
      sortField,
      sortOrder,
      withAssetsGenerationTools,
    }) =>
    ({ getState, setState, dispatch }, { currentCustomerId }) => {
      if (!currentCustomerId) {
        dispatch(ACTIONS.clearStore());
        return Promise.reject(new Error('Unknown customer'));
      }

      const { projectsCursor } = getState();

      setState({ loadingMoreProjects: true });

      const filter = {
        currentUser: onlyForCurrentUser,
        favoriteOnly: favoritesOnly,
        types: projectTypes,
      };

      // For WTM projects we should use another query
      const query = shouldQueryAssociatedProjects ? queryAssociatedProjects : queryProjects;

      return query({
        customerId: currentCustomerId,
        workspaceId,
        cursor: projectsCursor,
        filter,
        projectFolderId,
        sortField,
        sortOrder,
        withAssetsGenerationTools,
      })
        .then((result) => {
          const { edges, pageInfo } = result;

          const { projects } = getState();
          const moreProjects = unwrapProjects(edges);

          setState({
            projects: [...projects, ...moreProjects],
            hasMoreProjects: pageInfo.hasNextPage,
            projectsCursor: pageInfo.endCursor,
          });

          return moreProjects;
        })
        .finally(() => {
          setState({ loadingMoreProjects: false });
        });
    },

  getNewProjectPromptData:
    () =>
    ({ getState }) => {
      return getState().newProjectPromptData;
    },
  updateNewProjectPromptData:
    (value) =>
    ({ setState }) => {
      setState({ newProjectPromptData: value });
    },
  clearNewProjectPromptData:
    () =>
    ({ setState }) => {
      setState({ newProjectPromptData: null });
    },

  getPreClassifiedPromptText:
    () =>
    ({ getState }) => {
      return getState().preClassifiedPromptText;
    },
  setPreClassifiedPromptText:
    (value) =>
    ({ setState }) => {
      setState({ preClassifiedPromptText: value });
    },
  clearPreClassifiedPromptText:
    () =>
    ({ setState }) => {
      setState({ preClassifiedPromptText: null });
    },

  getNewProjectGenerationToolOverride:
    () =>
    ({ getState }) => {
      return getState().newProjectGenerationToolOverride;
    },
  setNewProjectGenerationToolOverride:
    (value) =>
    ({ setState }) => {
      setState({ newProjectGenerationToolOverride: value });
    },
  clearNewProjectGenerationToolOverride:
    () =>
    ({ setState }) => {
      setState({ newProjectGenerationToolOverride: null });
    },

  queryCurrentProject:
    (id, options) =>
    async ({ getState, setState, dispatch }) => {
      // If current project is "barebone" - don't show loading animations,
      // we should switch to created project seamlessly
      const { currentProject } = getState();
      const isSameProject = currentProject?.id === id;
      const currentIsBarebone = isBareboneProject(currentProject);
      if (!currentIsBarebone) {
        setState({ loadingCurrentProject: true, currentProject: null });
      }

      // We need an abort controller to cancel pending requests, in case user would want to exit "project" page mid-flight
      const abortController = new AbortController();
      setState({ queryCurrentProjectAbortController: abortController });

      try {
        const project = await queryProject({ id }, { abortSignal: abortController.signal });

        // Determine which "blog" draft to load
        const lastBlogEditingDraft = last(getBlogProjectEditingDrafts(project));
        const draftIndex = options?.draftId || lastBlogEditingDraft?.draftId || 0;

        const projectWithAdditionalData = await queryAdditionalProjectData(
          { project },
          {
            abortSignal: abortController.signal,
          }
        );
        if (!isProjectReady(projectWithAdditionalData)) {
          throw new Error('Project is not ready');
        }

        const preparedProject = prepareLoadedProject(projectWithAdditionalData);

        setState({
          currentProject: preparedProject,
          isBlogProjectDraft: options?.isBlogProjectDraft || false,
          blogDraftId: draftIndex,
        });
        const parsedGenerationInfo = parseGenerationInfo(preparedProject);
        if (
          parsedGenerationInfo?.tovInput &&
          (parsedGenerationInfo.tovInput.customId || parsedGenerationInfo.tovInput.name)
        ) {
          dispatch(
            ACTIONS.updateToneOfVoice(
              String(parsedGenerationInfo.tovInput.customId || parsedGenerationInfo.tovInput.name)
            )
          );
        }
        if (parsedGenerationInfo?.customModelId) {
          dispatch(ACTIONS.setSelectedCustomModelId(String(parsedGenerationInfo.customModelId)));
        }

        return preparedProject;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }
        throw error;
      } finally {
        setState({
          loadingCurrentProject: false,
          queryCurrentProjectAbortController: null,
        });

        // this action can be called to refresh the current project, in this case we should NOT reset the empty flag,
        // because it might influence the "back" button logic
        if (!isSameProject) {
          dispatch(ACTIONS.resetCurrentProjectEmptyFlag());
        }
      }
    },

  toggleProjectFavorite:
    (id) =>
    async ({ getState, dispatch }) => {
      const { projects } = getState();
      const project = projects.find((p) => p.id === id);
      if (!project) {
        return;
      }

      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const result = await updateProjectFavorite({ projectId: id, favorite: !project.favorite });
        dispatch(
          PRIVATE_ACTIONS.mergeProjectInCache({
            ...project,
            favorite: !project.favorite,
          })
        );
        return result;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  initBareboneCurrentProject:
    (projectType) =>
    ({ setState }) => {
      const bareboneProject = getBareboneProject(projectType);
      setState({ currentProject: bareboneProject });
      return bareboneProject;
    },

  getCurrentProject:
    () =>
    ({ getState }) => {
      return getState().currentProject;
    },

  hasMoreProjects:
    () =>
    ({ getState }) => {
      return getState().hasMoreProjects;
    },

  clearCurrentProject:
    () =>
    ({ getState, setState }) => {
      const { queryCurrentProjectAbortController } = getState();
      if (queryCurrentProjectAbortController) {
        queryCurrentProjectAbortController.abort();
      }

      setState({ currentProject: null });
    },

  updateCurrentProjectAgentConversationId:
    (externalConversationId) =>
    ({ getState, setState }) => {
      const { currentProject } = getState();
      const newValue = produce(currentProject, (draft) => {
        draft.conversation = {
          ...draft.conversation,
          externalConversationId,
        };
      });
      setState({ currentProject: newValue });
    },

  renameProject:
    ({ id, name }) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const newProject = await updateProject({ id, name });
        const prepared = prepareLoadedProject(newProject);

        dispatch(PRIVATE_ACTIONS.mergeProjectInCache(prepared));
        return prepared;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  moveProject:
    ({ id, projectFolderId }) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const newProject = await updateProject({ id, projectFolderId, updateProjectFolder: true });
        const prepared = prepareLoadedProject(newProject);

        dispatch(PRIVATE_ACTIONS.mergeProjectInCache(prepared));
        return prepared;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  updateProjectBrandVoicesForBrandTerms:
    ({ id, brandVoiceIds }) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const newProject = await updateProjectBrandVoicesForBrandTerms({
          id,
          brandVoiceIdsForBrandTerms: brandVoiceIds,
        });
        const prepared = prepareLoadedProject(newProject);

        dispatch(PRIVATE_ACTIONS.mergeProjectInCache(prepared));
        return prepared;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  updateProject:
    ({ id, name, projectFolderId }) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const newProject = await updateProject({
          id,
          name,
          projectFolderId,
          updateProjectFolder: true,
        });
        const prepared = prepareLoadedProject(newProject);

        dispatch(PRIVATE_ACTIONS.mergeProjectInCache(prepared));
        return prepared;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  deleteProject:
    (id, ifEmpty = false) =>
    async ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, true));
      try {
        const deletedProject = await deleteProject({ projectId: id, ifEmpty });
        // With `ifEmpty` the backend decides, and it answers with nothing when it finds the project
        // is not empty after all. Dropping it from the cache then would hide a project that still
        // exists until the next refetch.
        if (!deletedProject) {
          return null;
        }

        const prepared = prepareLoadedProject(deletedProject);

        dispatch(PRIVATE_ACTIONS.deleteProjectFromCache(prepared));
        return prepared;
      } finally {
        dispatch(PRIVATE_ACTIONS.markProjectProcessing(id, false));
      }
    },

  storeProject:
    (project) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.mergeProjectInCache(project));
    },

  clearStore:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  /**
   * Clears the store when switching customers, but preserves the current project
   * if it belongs to the new customer (e.g., when navigating via a project URL
   * that triggers a customer switch).
   */
  clearStoreForCustomerChange:
    (newCustomerId) =>
    ({ getState, setState }) => {
      const { currentProject, loadingCurrentProject, isBlogProjectDraft, blogDraftId } = getState();
      const projectCustomerId = currentProject?.customer?.id;

      // If the current project belongs to the new customer, preserve it
      // This happens when navigating to a project URL that triggers a customer switch
      if (projectCustomerId && projectCustomerId === newCustomerId) {
        const newState = cloneDeep(INITIAL_STATE);
        // Preserve current project related state
        newState.currentProject = currentProject;
        newState.loadingCurrentProject = loadingCurrentProject;
        newState.isBlogProjectDraft = isBlogProjectDraft;
        newState.blogDraftId = blogDraftId;
        setState(newState);
      } else {
        // Project doesn't belong to new customer, clear everything
        setState(cloneDeep(INITIAL_STATE));
      }
    },

  isCurrentProjectEmpty:
    () =>
    ({ getState }) =>
      getState().isCurrentProjectEmpty,

  markCurrentProjectAsNonEmpty:
    () =>
    ({ setState }) => {
      setState({ isCurrentProjectEmpty: false });
    },

  resetCurrentProjectEmptyFlag:
    () =>
    ({ setState }) => {
      setState({ isCurrentProjectEmpty: true });
    },

  getToneOfVoiceParams:
    () =>
    ({ getState }) => {
      const { selectedTov, selectedCustomModelId, draftToneOfVoice } = getState();

      return {
        tov: selectedTov,
        customModelId: selectedCustomModelId,
        draftToneOfVoice,
      };
    },

  setToneOfVoiceParams:
    (tovParams) =>
    ({ setState }) => {
      setState({
        selectedTov: tovParams.tov,
        selectedCustomModelId: tovParams.customModelId,
        draftToneOfVoice: tovParams.draftToneOfVoice,
      });
    },

  getSelectedTov:
    () =>
    ({ getState }) => {
      return getState().selectedTov;
    },

  updateToneOfVoice:
    (selectedTov) =>
    ({ setState }) => {
      setState({
        selectedTov,
        selectedCustomModelId: null,
      });
    },

  clearTovInputs:
    () =>
    ({ setState, dispatch }) => {
      setState({
        selectedCustomModelId: null,
        selectedTov: DEFAULT_SUGGEST_TOV_MODE,
      });
      dispatch(PRIVATE_ACTIONS.clearToneOfVoiceError());
    },

  setSelectedCustomModelId:
    (selectedCustomModelId) =>
    ({ setState, dispatch }) => {
      setState({
        selectedCustomModelId,
        selectedTov: null,
      });
      dispatch(PRIVATE_ACTIONS.clearToneOfVoiceError());
    },

  getSelectedCustomModelId:
    () =>
    ({ getState }) => {
      return getState().selectedCustomModelId;
    },

  clearSelectedCustomModelId:
    () =>
    ({ setState }) => {
      setState({
        selectedCustomModelId: null,
      });
    },

  isLoadingProjects:
    () =>
    ({ getState }) => {
      const { loadingProjects, loadingMoreProjects } = getState();
      return loadingProjects || loadingMoreProjects;
    },

  setDraftToneOfVoice:
    (draftToneOfVoice) =>
    ({ setState, dispatch }) => {
      setState({
        selectedTov: DRAFT_TOV_ID,
        selectedCustomModelId: null,
        draftToneOfVoice: { ...draftToneOfVoice, name: '' },
      });
      dispatch(PRIVATE_ACTIONS.clearToneOfVoiceError());
    },

  getDraftToneOfVoice:
    () =>
    ({ getState }) => {
      return getState().draftToneOfVoice;
    },

  clearDraftToneOfVoice:
    () =>
    ({ setState }) => {
      setState({
        draftToneOfVoice: null,
      });
    },

  selectDraftToneOfVoice:
    () =>
    ({ setState, dispatch }) => {
      setState({
        selectedTov: DRAFT_TOV_ID,
        selectedCustomModelId: null,
      });
      dispatch(PRIVATE_ACTIONS.clearToneOfVoiceError());
    },

  openToneOfVoiceMenu:
    () =>
    ({ setState, dispatch }) => {
      setState({
        toneOfVoiceMenuOpened: true,
      });
      dispatch(PRIVATE_ACTIONS.clearToneOfVoiceError());
    },
  closeToneOfVoiceMenu:
    () =>
    ({ getState, setState }) => {
      const { selectedTov, selectedCustomModelId } = getState();
      if (!selectedTov && !selectedCustomModelId) {
        setState({ toneOfVoiceError: 'Select tone of voice' });
      }
      setState({
        toneOfVoiceMenuOpened: false,
      });
    },

  getSelectedImageStyleId:
    () =>
    ({ getState }) => {
      return getState().selectedImageStyleId;
    },
  selectImageStyle:
    (imageStyle) =>
    ({ setState }) => {
      setState({ selectedImageStyleId: imageStyle?.id });
    },
  selectImageStyleId:
    (imageStyleId) =>
    ({ setState }) => {
      setState({ selectedImageStyleId: imageStyleId });
    },
  initImageStyle:
    (generationTool) =>
    ({ setState }) => {
      // clear selected image style for "image" content improver
      if (isRewriteGenerationTool(generationTool)) {
        setState({ selectedImageStyleId: null });
      } else {
        setState({ selectedImageStyleId: DEFAULT_IMAGE_STYLE_ID });
      }
    },

  getImageAspectRatio:
    () =>
    ({ getState }) => {
      return getState().imageAspectRatio;
    },
  setImageAspectRatio:
    (imageAspectRatio) =>
    ({ setState }) => {
      setState({ imageAspectRatio });
    },
  clearImageAspectRatio:
    () =>
    ({ setState }) => {
      setState({ imageAspectRatio: DEFAULT_IMAGE_ASPECT_RATIO });
    },

  getImageOverlayProperties:
    () =>
    ({ getState }) => {
      return getState().imageOverlayProperties;
    },
  setImageOverlayProperties:
    (props) =>
    ({ setState }) => {
      setState({ imageOverlayProperties: props });
    },
  clearImageOverlayProperties:
    () =>
    ({ setState }) => {
      setState({ imageOverlayProperties: initImageOverlayProps() });
    },

  initImageGenerationToolOptionsCache:
    (generationTool, subTemplateId) =>
    ({ dispatch }) => {
      const isYouTubeTool = generationTool.includes(SOCIAL_CHANNEL.youtube);
      const isMixtureEmailTool = generationTool === GENERATION_TOOL.emailMixture;

      const aspectRatio =
        isMixtureEmailTool || isYouTubeTool
          ? IMAGE_ASPECT_RATIO.ratio3x2
          : DEFAULT_IMAGE_ASPECT_RATIO;

      dispatch(ACTIONS.clearImageOverlayProperties());
      dispatch(ACTIONS.setImageAspectRatio(aspectRatio));
      dispatch(ACTIONS.restoreImageGenerationToolOptionsCache(generationTool, subTemplateId));
    },

  restoreImageGenerationToolOptionsCache:
    (generationTool, subTemplateId) =>
    ({ getState, setState }) => {
      const { imageGenerationToolOptionsCache: cache } = getState();

      const cacheKey = toolCacheKey(generationTool, subTemplateId);
      if (!cache[cacheKey]) {
        return;
      }

      if (cache[cacheKey].imageAspectRatio) {
        setState({ imageAspectRatio: cache[cacheKey].imageAspectRatio });
      }

      if (cache[cacheKey].imageOverlayProperties) {
        setState({ imageOverlayProperties: cache[cacheKey].imageOverlayProperties });
      }
    },

  updateImageGenerationToolOptionsCache:
    (generationTool, subTemplateId) =>
    ({ getState, setState }) => {
      const {
        imageAspectRatio,
        imageOverlayProperties,
        imageGenerationToolOptionsCache: cache,
      } = getState();

      const cacheKey = toolCacheKey(generationTool, subTemplateId);

      // For tools with sub templates, need to generate cache key only for selected template
      if (!cacheKey) {
        return;
      }

      setState({
        imageGenerationToolOptionsCache: {
          ...cache,
          [cacheKey]: { imageAspectRatio, imageOverlayProperties },
        },
      });
    },
};

const toolCacheKey = (generationTool, subTemplateId) => `${generationTool}_${subTemplateId}`;

export const ProjectStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
